import React, { useState, useEffect } from 'react';
import {
  Button,
  Label,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormText,
} from 'reactstrap';

/**
 * Modal to create or edit a banner
 */
function BannerModal({
  title = 'Banner',
  isOpen = false,
  onToggle = () => {},
  defaultValue = null,
  onSubmit = () => {},
}) {
  const [titulo, setTitulo] = useState('');
  const [subtitulo, setSubtitulo] = useState('');
  const [callAction, setCallAction] = useState(false);
  const [nombre, setNombre] = useState('');
  const [link, setLink] = useState('');
  const [imagen, setImagen] = useState(null);
  const [preview, setPreview] = useState(null);

  // Update fields when defaultValue changes (for edit mode)
  useEffect(() => {
    if (defaultValue) {
      setTitulo(defaultValue.titulo || '');
      setSubtitulo(defaultValue.subtitulo || '');
      setCallAction(defaultValue.call_action || false);
      setNombre(defaultValue.nombre || '');
      setLink(defaultValue.link || '');
      setPreview(defaultValue.imagen_url || null);
    } else {
      // Reset form for create mode
      setTitulo('');
      setSubtitulo('');
      setCallAction(false);
      setNombre('');
      setLink('');
      setImagen(null);
      setPreview(null);
    }
  }, [defaultValue, isOpen]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImagen(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    if (!titulo.trim() || !subtitulo.trim()) {
      return;
    }

    if (callAction && (!nombre.trim() || !link.trim())) {
      return;
    }

    // If editing and no new image, image is not required
    if (!defaultValue && !imagen) {
      return;
    }

    // Create FormData
    const formData = new FormData();
    formData.append('titulo', titulo.trim());
    formData.append('subtitulo', subtitulo.trim());
    formData.append('call_action', callAction ? 'true' : 'false');

    if (callAction) {
      formData.append('nombre', nombre.trim());
      formData.append('link', link.trim());
    }

    // Only append image if it's a new file (for create) or if user uploaded a new one (for edit)
    if (imagen) {
      formData.append('imagen', imagen);
    }

    onSubmit(formData);
  };

  const handleCancel = () => {
    // Reset to original values
    if (defaultValue) {
      setTitulo(defaultValue.titulo || '');
      setSubtitulo(defaultValue.subtitulo || '');
      setCallAction(defaultValue.call_action || false);
      setNombre(defaultValue.nombre || '');
      setLink(defaultValue.link || '');
      setPreview(defaultValue.imagen_url || null);
    } else {
      setTitulo('');
      setSubtitulo('');
      setCallAction(false);
      setNombre('');
      setLink('');
      setImagen(null);
      setPreview(null);
    }
    onToggle(); // Close modal
  };

  return (
    <Modal isOpen={isOpen} fade={false} toggle={onToggle} size="lg">
      <ModalHeader toggle={onToggle}>{title}</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label htmlFor="titulo">Título *</Label>
          <Input
            id="titulo"
            type="text"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Ej: Oferta Especial"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="subtitulo">Subtítulo *</Label>
          <Input
            id="subtitulo"
            type="text"
            value={subtitulo}
            onChange={(e) => setSubtitulo(e.target.value)}
            placeholder="Ej: Descubre nuestras mejores ofertas"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="imagen">
            Imagen {!defaultValue ? '*' : ''} (450x200px)
          </Label>
          <Input
            id="imagen"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
          <FormText color="muted">
            La imagen será redimensionada automáticamente a 450x200px
          </FormText>
          {preview && (
            <div className="mt-2">
              <img
                src={preview}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: '200px' }}
              />
            </div>
          )}
        </FormGroup>

        <FormGroup check>
          <Label check>
            <Input
              type="checkbox"
              checked={callAction}
              onChange={(e) => setCallAction(e.target.checked)}
            />
            Habilitar Call to Action
          </Label>
        </FormGroup>

        {callAction && (
          <>
            <FormGroup>
              <Label htmlFor="nombre">Nombre del botón *</Label>
              <Input
                id="nombre"
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Ej: Ver más"
                disabled={!callAction}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="link">Link *</Label>
              <Input
                id="link"
                type="url"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="Ej: https://ejemplo.com"
                disabled={!callAction}
              />
            </FormGroup>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="success" onClick={handleSubmit}>
          Guardar
        </Button>
        <Button color="secondary" onClick={handleCancel}>
          Cancelar
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default BannerModal;
